import React, { useEffect } from "react";
import PropTypes from "prop-types";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import FocusLock from "react-focus-lock";
import { useIntl } from "react-intl";
import { FormattedMessage } from "@learningblocks/shared-components/lib/FormattedMessage";
import { sanitizeConfigOptions } from "@labxchange/util-sanitization";
import { ReactReduxContext } from "react-redux";

import CloseIcon from "@learningblocks/shared-components/lib/Icons/close.svg";
import { SingleSelect } from "@learningblocks/shared-components/lib/Questions/SingleSelect";
import { MultiSelectRedux } from "@learningblocks/simulations/shared-components";
import { MultiSelect } from "@learningblocks/shared-components/lib/Questions";
import { LearnMore } from "@learningblocks/interactives/interactive-infographic-components/lib/LearnMore";
import { isUsersnapActive } from "@labxchange/util-usersnap";
import QuestionTableWrapper from "./QuestionTableWrapper";

import className from "../style.module.scss";

export function WidgetWrapper(props) {
  const intl = useIntl();
  const reduxContext = React.useContext(ReactReduxContext);
  const hasMscqReducer = reduxContext?.store?.getState()?.mscq !== undefined;
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" || e.keyCode === 27) {
        closeWidget();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const closeWidget = () => {
    if (props.type === "learn-more") {
      props.handleTrigger(props.hotspotID);
    } else if (props.widgetProps.complete) {
      props.handleTrigger(props.hotspotID);
    }
    props.handleCloseWidget();
  };

  return (
    <FocusLock disabled={isUsersnapActive()}>
      <div
        role="dialog"
        aria-modal="true"
        className={`${className.widgetOverlay} ${props.closeWidget ? className.closing : ""}`}
      >
        <div className={className.widgetPin}>
          <ClickAwayListener
            mouseEvent="onMouseDown"
            onClickAway={() => {
              closeWidget();
            }}
          >
            <div
              className={`${className.widgetWrapper} ${props.closeWidget ? className.closing : ""}`}
            >
              <button
                className={`${className.closeBtn} ${className.button}`}
                tabIndex={0}
                onClick={() => {
                  closeWidget();
                }}
                aria-label={intl.formatMessage({
                  id: "Close",
                  defaultMessage: "Close",
                })}
              >
                <FormattedMessage string={"Close"} description="" />
                <img alt="" src={CloseIcon} />
              </button>
              <div className={className.widget}>
                {props.type === "text-body" ? (
                  <div className={className.textBodyWidget}>
                    <FormattedMessage
                      string={props.widgetProps.text}
                      sanitizeConfig={sanitizeConfigOptions.BlockText}
                      description=""
                    />
                  </div>
                ) : props.type === "CSCQ" ? (
                  <SingleSelect
                    {...props.widgetProps}
                    selectOption={(optionData) => {
                      props.handleCSCQ_OptionSelect(
                        props.hotspotID,
                        optionData
                      );
                    }}
                    submitAnswer={() => {
                      props.handleCSCQ_Submit(props.hotspotID);
                    }}
                    resetAnswer={() => {
                      // Reset functionality - interactive infographics don't typically need this
                      // but it's required by the simulations version
                    }}
                    preview={props.preview}
                  />
                ) : props.type === "MSCQ" ? (
                  hasMscqReducer ? (
                    <MultiSelectRedux
                      {...props.widgetProps}
                      selectOption={(chackboxData) => {
                        props.handleMSCQRedux_OptionSelect(
                          props.hotspotID,
                          chackboxData
                        );
                      }}
                      submitAnswer={() => {
                        props.handleMSCQRedux_Submit(props.hotspotID);
                      }}
                      onCompleteChange={(isComplete) => {
                        props.handleMSCQRedux_Submit(
                          props.hotspotID,
                          isComplete
                        );
                      }}
                      preview={props.preview}
                    />
                  ) : (
                    <MultiSelect
                      {...props.widgetProps}
                      selectOption={(chackboxData) => {
                        props.handleMSCQ_OptionSelect(
                          props.hotspotID,
                          chackboxData
                        );
                      }}
                      submitAnswer={() => {
                        props.handleMSCQ_Submit(props.hotspotID);
                      }}
                      enableAllOptions={() => {
                        props.handleMSCQ_EnableAll(props.hotspotID);
                      }}
                      resetAnswer={() => {
                        props.handleMSCQ_Reset(props.hotspotID);
                      }}
                      preview={props.preview}
                    />
                  )
                ) : props.type === "learn-more" ? (
                  <LearnMore {...props.widgetProps} preview={props.preview} />
                ) : props.type === "TABLE_QUESTION" ? (
                  <QuestionTableWrapper
                    widgetProps={props.widgetProps}
                    hotspotID={props.hotspotID}
                    preview={props.preview}
                    handleTableQuestion_Submit={
                      props.handleTableQuestion_Submit
                    }
                  />
                ) : null}
              </div>
            </div>
          </ClickAwayListener>
        </div>
      </div>
    </FocusLock>
  );
}

WidgetWrapper.propTypes = {
  closeWidget: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired,
  widgetProps: PropTypes.object.isRequired,
  handleCloseWidget: PropTypes.func.isRequired,
  preview: PropTypes.bool,
  handleCSCQ_OptionSelect: PropTypes.func.isRequired,
  handleCSCQ_Submit: PropTypes.func.isRequired,
  handleTableQuestion_Submit: PropTypes.func.isRequired,
  handleMSCQ_OptionSelect: PropTypes.func.isRequired,
  handleMSCQ_Submit: PropTypes.func.isRequired,
  handleMSCQ_EnableAll: PropTypes.func.isRequired,
  handleMSCQ_Reset: PropTypes.func.isRequired,
  handleMSCQRedux_OptionSelect: PropTypes.func.isRequired,
  handleMSCQRedux_Submit: PropTypes.func.isRequired,
};
WidgetWrapper.defaultProps = {
  preview: false,
};
